/* Criar / editar / ver uma versão de documento legal (ADR-0774).

   NÃO EXISTE campo de versão. O número é inteiro monotônico calculado no servidor (`max+1` por
   docKey): um input de versão seria um bug de contrato, não uma conveniência. A única versão que o
   operador escolhe é a ORIGEM da cópia (`copyFromVersion`), e ela cria uma versão nova de verdade.

   O modo é DERIVADO do estado da versão, não passado pelo caller: `group` publicado ⇒ somente
   leitura, sempre. Assim nenhuma tela consegue abrir um form editável sobre texto publicado — a
   imutabilidade não depende de o caller lembrar de passar uma flag.

   Regra de ouro (ADR-0216): `locked` enquanto a mutation está em flight. */
import {
  createLegalVersionBody,
  LEGAL_FALLBACK_LOCALE,
  LEGAL_LOCALES,
  type LegalDocKey,
  updateLegalVersionBody,
} from '@planuze/admin-contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InlineAlert,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@planuze/ui';
import { useState } from 'react';
import { useAdminLocale } from '../../content/locale-provider.js';
import {
  type LegalAdminContent,
  useLegalAdminContent,
} from '../../content/sections/legal-admin.js';
import { useCreateLegalVersion, useSaveLegalDraft } from '../../hooks/use-legal.js';
import { type LegalVersionGroup, missingLegalLocales } from '../../lib/legal-versions.js';
import { legalErrorMessage } from './legal-error.js';
import { LegalLocaleFields } from './legal-locale-fields.js';

/** Sentinela do seletor de origem: escrever o texto agora em vez de copiar uma versão. */
const MANUAL_SOURCE = 'manual';

function localeLabel(locale: string, labels: Readonly<Record<string, string>>): string {
  return labels[locale] ?? locale;
}

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly docKey: LegalDocKey;
  /** Versões existentes — alimentam o seletor de origem da cópia. */
  readonly groups: readonly LegalVersionGroup[];
  /** Ausente = criar. Presente = editar (rascunho) ou ver (publicada, somente leitura). */
  readonly group?: LegalVersionGroup | undefined;
  /** Versão a pré-selecionar como origem ao criar (atalho "nova versão a partir desta"). */
  readonly copyFromVersion?: number | undefined;
  /**
   * O operador pode gravar? MANAGE é `super_admin` apenas.
   *
   * Sem isto, um tier de leitura clicando "Ver" num RASCUNHO recebia formulário editável (a
   * tabela ramifica por `isDraft && canManage`, mas `onView` e `onEdit` abrem o mesmo estado). O
   * servidor recusaria o PUT com 403, então era a UI oferecendo uma ação impossível.
   */
  readonly canManage: boolean;
}

interface LocaleDraft {
  readonly locale: string;
  readonly title: string;
  readonly content: string;
  /** Presente = linha já persistida (o save é um PUT nela). Ausente = idioma novo. */
  readonly existingId?: string;
}

function buildDrafts(group: LegalVersionGroup | undefined): LocaleDraft[] {
  if (group === undefined) {
    return LEGAL_LOCALES.map((locale) => ({ locale, title: '', content: '' }));
  }
  /* Edição/leitura mostra só os idiomas QUE EXISTEM: o update é por linha e não cria linha, então um
     campo para idioma ausente seria um campo que não salva. O aviso de faltantes é separado. */
  return group.locales.map((row) => ({
    locale: row.locale,
    title: row.title,
    content: row.content,
    existingId: row.id,
  }));
}

/**
 * Título do modal.
 *
 * Função e não ternário aninhado porque há TRÊS estados e a razão da leitura importa: versão
 * publicada é imutável por contrato, e rascunho sem permissão é limitação de papel. Dizer
 * "publicada" num rascunho confundiria o operador sobre o que ele está vendo.
 */
function modalTitle(
  group: LegalVersionGroup | undefined,
  canManage: boolean,
  copy: LegalAdminContent['versionFormModal'],
): string {
  if (group === undefined) return copy.titleNew;
  if (group.status === 'published') return copy.titlePublishedReadOnly(group.version);
  if (!canManage) return copy.titleDraftReadOnly(group.version);
  return copy.titleEditDraft(group.version);
}

function isFilled(draft: LocaleDraft): boolean {
  return draft.title.trim() !== '' && draft.content.trim() !== '';
}

export function LegalVersionFormModal({
  open,
  onClose,
  docKey,
  groups,
  group,
  copyFromVersion,
  canManage,
}: Props) {
  const isEdit = group !== undefined;
  /* Duas razões INDEPENDENTES para ser somente-leitura: o dado (versão publicada é imutável) e o
     papel (tier de leitura não grava). A primeira é invariante de contrato, a segunda é permissão
     — e faltar qualquer uma delas oferece uma ação que o servidor recusa. */
  const readOnly = !canManage || (isEdit && group.status === 'published');
  const [drafts, setDrafts] = useState<LocaleDraft[]>(() => buildDrafts(group));
  const [source, setSource] = useState(
    copyFromVersion === undefined ? MANUAL_SOURCE : String(copyFromVersion),
  );
  const [preview, setPreview] = useState(readOnly);
  const [error, setError] = useState<string | null>(null);
  const { locale: adminLocale } = useAdminLocale();
  const copy = useLegalAdminContent().versionFormModal;

  const create = useCreateLegalVersion(docKey);
  const save = useSaveLegalDraft(docKey);
  const busy = create.isPending || save.isPending;
  const copying = !isEdit && source !== MANUAL_SOURCE;
  const missing = isEdit ? missingLegalLocales(group) : [];
  const firstTab = drafts[0]?.locale ?? LEGAL_LOCALES[0];

  const patchDraft = (
    locale: string,
    patch: { readonly title?: string; readonly content?: string },
  ): void => {
    setDrafts((prev) => prev.map((d) => (d.locale === locale ? { ...d, ...patch } : d)));
  };

  const submitCreate = (): void => {
    /* Valida com o PRÓPRIO schema do contrato (inclui o XOR locales/copyFromVersion e os caps de
       tamanho) — a UI não reimplementa a regra que o servidor aplica. Note que nenhum dos dois ramos
       carrega `version`: o número é do servidor. */
    const parsed = createLegalVersionBody.safeParse(
      copying
        ? { copyFromVersion: Number(source) }
        : {
            locales: drafts
              .filter(isFilled)
              .map((d) => ({ locale: d.locale, title: d.title, content: d.content })),
          },
    );
    if (!parsed.success) {
      setError(copy.missingFieldsError);
      return;
    }
    create.mutate(parsed.data, {
      onSuccess: (created) => {
        const version = created[0]?.version;
        toast.success(
          version === undefined
            ? copy.draftCreatedToast
            : copy.draftCreatedWithVersionToast(version),
        );
        onClose();
      },
      onError: (err) => setError(legalErrorMessage(err, adminLocale)),
    });
  };

  const submitSave = (): void => {
    const rows = drafts.flatMap((draft) =>
      draft.existingId === undefined
        ? []
        : [{ id: draft.existingId, title: draft.title, content: draft.content }],
    );
    for (const row of rows) {
      if (!updateLegalVersionBody.safeParse({ title: row.title, content: row.content }).success) {
        setError(copy.emptyFieldsError);
        return;
      }
    }
    save.mutate(rows, {
      onSuccess: () => {
        toast.success(copy.draftSavedToast);
        onClose();
      },
      onError: (err) => setError(legalErrorMessage(err, adminLocale)),
    });
  };

  const onSubmit = (): void => {
    setError(null);
    if (isEdit) submitSave();
    else submitCreate();
  };

  const title = modalTitle(group, canManage, copy);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !busy && onClose()}>
      <DialogContent locked={busy} className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {readOnly ? copy.readOnlyDescription : copy.editableDescription}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {error !== null && <InlineAlert variant="destructive">{error}</InlineAlert>}
          {readOnly && <InlineAlert variant="warning">{copy.publishedWarning}</InlineAlert>}
          {missing.length > 0 &&
            (missing.includes(LEGAL_FALLBACK_LOCALE) ? (
              /* Caso GRAVE, e o oposto do outro: sem a linha de pt-BR a leitura pública não tem
                 para onde cair e responde 404 — a página de termos fica fora do ar no idioma
                 default. O servidor recusa o publish (`legal_fallback_locale_required`); aqui o
                 operador descobre antes de tentar. */
              <InlineAlert variant="destructive">
                {copy.missingFallbackWarning(LEGAL_FALLBACK_LOCALE)}
              </InlineAlert>
            ) : (
              <InlineAlert variant="warning">
                {copy.missingOtherWarning(missing.join(', '), LEGAL_FALLBACK_LOCALE)}
              </InlineAlert>
            ))}
          {!isEdit && (
            <div className="flex w-full max-w-sm flex-col gap-1.5">
              <Label>{copy.sourceLabel}</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger aria-label={copy.sourceAriaLabel}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={MANUAL_SOURCE}>{copy.writeNowOption}</SelectItem>
                  {groups.map((candidate) => (
                    <SelectItem key={candidate.version} value={String(candidate.version)}>
                      {copy.copyFromVersionOption(candidate.version)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {copying ? (
            <InlineAlert variant="info">{copy.copyingInfo(source)}</InlineAlert>
          ) : (
            <>
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => setPreview((p) => !p)}>
                  {preview ? (readOnly ? copy.showMarkdown : copy.editMarkdown) : copy.preview}
                </Button>
              </div>
              <Tabs defaultValue={firstTab}>
                <TabsList>
                  {drafts.map((draft) => (
                    <TabsTrigger key={draft.locale} value={draft.locale}>
                      {localeLabel(draft.locale, copy.localeLabels)}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {drafts.map((draft) => (
                  <TabsContent key={draft.locale} value={draft.locale}>
                    <LegalLocaleFields
                      locale={draft.locale}
                      title={draft.title}
                      content={draft.content}
                      readOnly={readOnly}
                      preview={preview}
                      onChange={(patch) => patchDraft(draft.locale, patch)}
                    />
                  </TabsContent>
                ))}
              </Tabs>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {readOnly ? copy.close : copy.cancel}
          </Button>
          {/* Versão publicada NÃO recebe botão de salvar: a ação não existe, não está desabilitada. */}
          {!readOnly && (
            <Button onClick={onSubmit} loading={busy}>
              {isEdit ? copy.saveDraft : copy.createDraft}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
